/**
 * Portal guide, stage 2 — the ids of the guide targets (`data-guide-id`).
 *
 * A guide target is an element the assistant must be able to point at: a sidebar entry, a tab,
 * a create button. The target carries `data-guide-id="<id>"`, the registry
 * (`src/guide/registry/*.json`, field `guideId`) names the same id, and the spotlight finds the
 * element via `document.querySelectorAll('[data-guide-id="…"]')`.
 *
 * Two kinds of ids:
 * 1. **Navigation** — `nav.<key>`, where `<key>` is the area key from
 *    `src/hooks/useSettingsNavigation.tsx` (the same value as `?panel=<key>`). The id is created
 *    in the loop of `src/components/AppSidebar.tsx`; it covers all sidebar entries at once,
 *    without a list having to be maintained here.
 *    Settings and administration share some keys (`billing`, `telemetry`, `statistics`,
 *    `teamManagement`): that is intended, because only one of the two sidebars is ever in the
 *    document — which one is decided by the address.
 * 2. **Single targets** — fixed, hierarchical ids (`settings.apiKeys.create`) that are listed
 *    below and attached literally to the button in the source.
 *
 * **Several elements may carry the same id, even at the same time.** A create button usually
 * appears twice in the portal: above the table (always) and in the table's empty state
 * (`TableEmptyState`, additionally, while the list is empty) — then both are in the document.
 * The header's „Fragen" button also appears twice, mobile and desktop; here a media query
 * decides, not rendering. The spotlight therefore takes the first **visible and measured** hit
 * (`GuideSpotlight.tsx`, `isVisibleTarget` + `isMeasurable`). Whoever adds a double target
 * should still put the preferred one first in the document — then the order is also right for
 * clients without a visibility check.
 *
 * ## Names
 *
 * The second-to-last segment is the sidebar's **area key** (`?panel=<key>`, the same value as in
 * `nav.<key>`): `settings.teamManagement.invite`, not `settings.team.invite`. If the registry
 * entry also carries a `guideAction` (prefill, package B5), it carries the **same** id as
 * `guideId` — one button, one name; a guard in `featureRegistry.test.ts` enforces this.
 */

/**
 * Pattern of an id: dot notation, first character lowercase. Hyphens are allowed from the second
 * segment on, because the CBOS area keys contain some (`nav.portfolio-dashboard`).
 * No `g` flag: `RegExp.test` would otherwise be stateful.
 */
export const GUIDE_ID_PATTERN = /^[a-z][a-zA-Z0-9]*(?:\.[a-zA-Z0-9-]+)*$/;

/** Id of a sidebar entry. */
export type NavGuideId = `nav.${string}`;

/** `nav.<key>` — the area key is the `?panel=` value or the CBOS route name. */
export function navGuideId(key: string): NavGuideId {
  return `nav.${key}`;
}

/**
 * The ids of the single targets. The nesting mirrors the dot notation; the value is always the
 * full id, so it can be searched for in the source exactly as it appears here.
 *
 * Not included, although named in PORTAL_GUIDE.md §6.9:
 * - `settings.apiKeys.createEmpty` — the button in the empty state carries
 *   `settings.apiKeys.create` (see header comment); two ids for the same button would be two
 *   names for one target.
 * - `settings.billing.createInvoice` — the button exists, but its `onClick` is empty
 *   (`src/settings/BillingPanel.tsx`); a target that does nothing is not a guide target.
 * - `admin.gateway.new` — the gateway administration has no button that creates a new gateway;
 *   the create view is not reachable in the existing code.
 */
export const GUIDE_IDS = {
  /** Sidebar: `GUIDE_IDS.nav('agent')` → `'nav.agent'`. */
  nav: navGuideId,

  header: {
    /** The header's „Fragen" button — the guide must be able to point at itself. */
    assistant: 'header.assistant',
  },

  settings: {
    agent: {
      tabApiKeys: 'settings.agent.tab.apiKeys',
      createToolset: 'settings.agent.toolset.create',
      createKnowledgeBase: 'settings.agent.knowledgeBase.create',
    },
    apiKeys: {
      create: 'settings.apiKeys.create',
    },
    alerts: {
      create: 'settings.alerts.create',
      /**
       * Condition and action in the alert editor. Both are attached to the button, but to **no**
       * registry entry: the registry lists the editor under a single entry „Bedingungen und
       * Aktionen bearbeiten". Until it is split, the assistant cannot point at them — whoever
       * splits the entry adds nothing here, only `guideId` there.
       */
      addCondition: 'settings.alerts.addCondition',
      addAction: 'settings.alerts.addAction',
      /** The button that adds a push notification to the rule; the registry has an entry of its own for it. */
      addPush: 'settings.alerts.addPush',
    },
    teamManagement: {
      invite: 'settings.teamManagement.invite',
      createRole: 'settings.teamManagement.createRole',
    },
    projects: {
      create: 'settings.projects.create',
      addSite: 'settings.projects.addSite',
      addPartner: 'settings.projects.addPartner',
    },
    productManagement: {
      create: 'settings.productManagement.create',
      /**
       * Only here now, no longer in the registry: the „Telemetrie" tab is not addressable
       * (`?tab` is written, never read), and the panel only reads `?product` on mount — so
       * after a navigation within `/settings` the target does not show up even with the
       * parameter filled. The entry describes the way in words instead.
       */
      addTelemetryField: 'settings.productManagement.addTelemetryField',
    },
    operatorObligations: {
      createBinding: 'settings.operatorObligations.createBinding',
      createChangeFeed: 'settings.operatorObligations.createChangeFeed',
    },
    securityProfile: {
      changePassword: 'settings.securityProfile.changePassword',
      mfa: 'settings.securityProfile.mfa',
      authApp: 'settings.securityProfile.authApp',
    },
    userProfile: {
      edit: 'settings.userProfile.edit',
      /** The switches of the notification preferences (alerts, tasks, broadcasts). */
      notifications: 'settings.userProfile.notifications',
    },
    pushDevices: {
      /** The "Senden" button of the broadcast message. */
      broadcast: 'settings.pushDevices.broadcast',
    },
    api: {
      /** The telemetry upload of the API area — a section, not a button. */
      ingest: 'settings.api.ingest',
    },
    processManagement: {
      createChecklist: 'settings.processManagement.createChecklist',
      start: 'settings.processManagement.start',
    },
    assetInventory: {
      unassigned: 'settings.assetInventory.unassigned',
    },
    tenderSearchProfiles: {
      create: 'settings.tenderSearchProfiles.create',
    },
  },

  entity: {
    building: {
      createFloor: 'entity.building.createFloor',
    },
    floor: {
      createRoom: 'entity.floor.createRoom',
    },
  },
} as const;

/** All string leaves of a nested id tree. */
type StringLeaves<T> = T extends string ? T : { [K in keyof T]: StringLeaves<T[K]> }[keyof T];

/** The fixed ids of the single targets (without navigation — that one is open-ended). */
export type FixedGuideId = StringLeaves<Omit<typeof GUIDE_IDS, 'nav'>>;

/** Every valid id: a single target or a sidebar entry. */
export type GuideId = FixedGuideId | NavGuideId;

/** The same fixed ids at runtime, sorted — the basis of the guards. */
export const FIXED_GUIDE_IDS: readonly string[] = (() => {
  const found: string[] = [];
  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      found.push(node);
      return;
    }
    if (node !== null && typeof node === 'object') {
      Object.keys(node as Record<string, unknown>).forEach((key) => walk((node as Record<string, unknown>)[key]));
    }
  };
  Object.keys(GUIDE_IDS).forEach((key) => {
    if (key === 'nav') return; // a function, not a leaf
    walk((GUIDE_IDS as Record<string, unknown>)[key]);
  });
  return found.sort();
})();

/**
 * The attribute for spreading: `<Button {...guideAttr(GUIDE_IDS.settings.apiKeys.create)}>`.
 *
 * On the target itself, the existing code uses the literal spelling `data-guide-id="…"` — the
 * guards search for it as text, and an id that only comes into being at runtime would not be
 * found. This helper is therefore meant for callers that already have the id in hand (spotlight
 * tests, dynamically generated lists).
 */
export function guideAttr(id: GuideId): { 'data-guide-id': GuideId } {
  return { 'data-guide-id': id };
}

/**
 * Targets whose click **writes immediately** — no dialog, no confirmation:
 * `FloorProperties.tsx` calls `createSpace(...)` directly in `onClick`,
 * `BuildingLocationProperties.tsx` calls `createFloor(...)` directly. The spotlight points at
 * them but does not focus them: on a focused button, the space bar — which people press to
 * scroll — creates a floor or a room, and stage 2 of the portal guide writes nothing
 * (CONTRACT.md §7).
 */
export const WRITES_ON_CLICK_GUIDE_IDS: readonly string[] = [
  GUIDE_IDS.entity.building.createFloor,
  GUIDE_IDS.entity.floor.createRoom,
];
